import { createI18n } from 'vue-i18n'

/**
 * THE list of app languages. Adding a language = drop `./locales/<code>.json`
 * next to en.json and add one row here (code → the language's own name, as a
 * speaker of it would write it). Nothing else in the app needs to change: the
 * pickers, the workspace-language mapping and the lazy loading all read this.
 *
 * Codes are two letters (the first two letters of a workspace language are
 * matched against them).
 */
const LOCALE_REGISTRY = {
  en: 'English',
  pt: 'Português (Brasil)',
} as const satisfies Record<string, string>

export type SupportedLocale = keyof typeof LOCALE_REGISTRY

const DEFAULT_LOCALE: SupportedLocale = 'en'

// Historical spellings that mean one of the registry languages. Same rule as
// the backend locale registry (backend/services/locale/registry.js): 'br' was
// once stored for Brazilian Portuguese.
const LOCALE_ALIASES: Record<string, SupportedLocale> = {
  br: 'pt',
}

// One lazy chunk per language file — only the active language is downloaded.
type LocaleMessages = Record<string, string | Record<string, unknown>>
const localeFiles = import.meta.glob<{ default: LocaleMessages }>('./locales/*.json')

function bundleLoaderFor(locale: string): (() => Promise<{ default: LocaleMessages }>) | undefined {
  return localeFiles[`./locales/${locale}.json`]
}

if (import.meta.env.DEV) {
  for (const code of Object.keys(LOCALE_REGISTRY)) {
    if (!bundleLoaderFor(code)) console.warn(`[i18n] "${code}" is listed but src/i18n/locales/${code}.json is missing`)
  }
}

/** Languages the app can show, in registry order (only those with a bundle). */
export const SUPPORTED_LOCALES: SupportedLocale[] = (Object.keys(LOCALE_REGISTRY) as SupportedLocale[])
  .filter(code => !!bundleLoaderFor(code))

/** Each language's own name, for pickers. */
export const LOCALE_LABELS: Record<SupportedLocale, string> = { ...LOCALE_REGISTRY }

/**
 * Languages a WORKSPACE can write its content in (Settings › Language) that the
 * app's own screens are not translated into yet. The backend accepts these for
 * a workspace (workspaceController update) and its locale registry writes
 * content in them. Once a language gets its own app bundle + registry row
 * above, its entry here is redundant and can be dropped.
 */
const WORKSPACE_ONLY_LANGUAGES: Record<string, string> = {
  es: 'Español',
  it: 'Italiano',
}

/** Workspace language picker rows: the app languages first, then content-only ones. */
export const WORKSPACE_LANGUAGES: { code: string; label: string }[] = [
  ...SUPPORTED_LOCALES.map(code => ({ code, label: LOCALE_LABELS[code] })),
  ...Object.entries(WORKSPACE_ONLY_LANGUAGES)
    .filter(([code]) => !(SUPPORTED_LOCALES as string[]).includes(code))
    .map(([code, label]) => ({ code, label })),
]

export function isSupportedLocale(value: unknown): value is SupportedLocale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as string[]).includes(value)
}

/**
 * Map any language code ('pt', 'pt-BR', 'PT', 'br', 'es', …) to a language we
 * ship: take the first two letters, apply the historical aliases, and send
 * everything without a bundle to English.
 */
export function matchLocale(value?: string | null): SupportedLocale {
  const key = String(value ?? '').trim().toLowerCase().slice(0, 2)
  const code = LOCALE_ALIASES[key] ?? key
  return isSupportedLocale(code) ? code : DEFAULT_LOCALE
}

/**
 * Resolve the actual UI locale given a user language preference and optional workspace language.
 * When userLanguage is 'auto', the locale is derived from the workspace language.
 */
export function resolveLocale(userLanguage: string, workspaceLanguage?: string): SupportedLocale {
  if (userLanguage === 'auto') {
    return matchLocale(workspaceLanguage)
  }
  return isSupportedLocale(userLanguage) ? userLanguage : DEFAULT_LOCALE
}

const i18n = createI18n({
  legacy: false,
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  messages: {}
})

const loadedLocales = new Set<SupportedLocale>()
const loadingLocales = new Map<SupportedLocale, Promise<void>>()

// Locale messages mix flat-key strings (`"common.save": "Save"`) used by the Vue
// app with nested objects (e.g. `builder`) consumed by the embedded builder.
function fetchLocaleMessages(locale: SupportedLocale): Promise<LocaleMessages> {
  const loader = bundleLoaderFor(locale) ?? bundleLoaderFor(DEFAULT_LOCALE)
  if (!loader) return Promise.resolve({})
  return loader().then(m => m.default)
}

/**
 * Ensure a locale's messages are loaded. Safe to call multiple times — caches the
 * in-flight promise so concurrent calls share the same network request.
 */
export function loadLocale(locale: SupportedLocale): Promise<void> {
  if (loadedLocales.has(locale)) return Promise.resolve()
  const existing = loadingLocales.get(locale)
  if (existing) return existing
  const promise = fetchLocaleMessages(locale).then(messages => {
    i18n.global.setLocaleMessage(locale, messages)
    loadedLocales.add(locale)
    loadingLocales.delete(locale)
  })
  loadingLocales.set(locale, promise)
  return promise
}

/**
 * Set the active locale. Loads the locale's messages on first use.
 * Returns a promise callers can await, but fire-and-forget is also fine —
 * the locale will switch as soon as the chunk arrives.
 */
export function setLocale(locale: SupportedLocale): Promise<void> {
  if (!isSupportedLocale(locale)) {
    console.warn(`Unsupported locale "${locale}", falling back to "${DEFAULT_LOCALE}"`)
    locale = DEFAULT_LOCALE
  }
  return loadLocale(locale).then(() => {
    i18n.global.locale.value = locale
  })
}

/**
 * Get the current active locale.
 */
export function getLocale(): SupportedLocale {
  return i18n.global.locale.value as SupportedLocale
}

export default i18n
